
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Check, Eye, Mail, Calendar, BarChart3 } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface MetricProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  description?: string;
  progress?: number;
  className?: string;
}

const MetricCard: React.FC<MetricProps> = ({ 
  title, 
  value, 
  icon, 
  description, 
  progress, 
  className = "" 
}) => {
  return (
    <Card className={`overflow-hidden ${className}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className="rounded-full bg-muted p-2">{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
        {progress !== undefined && (
          <Progress value={progress} className="h-2 mt-2" />
        )}
      </CardContent>
    </Card>
  );
};

interface PartnerDashboardMetricsProps {
  totalLeads: number;
  closedDeals: number;
  successRate: number;
  viewedSMS: number;
  sentSMS: number;
  viewRate: number;
  emailsSent?: number;
  emailsOpened?: number;
  emailOpenRate?: number;
  leadsLastWeek?: number;
  leadsLastMonth?: number;
}

const PartnerDashboardMetrics: React.FC<PartnerDashboardMetricsProps> = ({
  totalLeads,
  closedDeals,
  successRate,
  viewedSMS,
  sentSMS,
  viewRate,
  emailsSent = 0,
  emailsOpened = 0,
  emailOpenRate = 0,
  leadsLastWeek = 0,
  leadsLastMonth = 0,
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <MetricCard
        title="Total Leads"
        value={totalLeads}
        icon={<Users className="h-4 w-4 text-blue-500" />}
        description={leadsLastMonth > 0 ? `+${leadsLastMonth} leads this month` : "No new leads this month"}
        className="bg-blue-50/50"
      />
      
      <MetricCard
        title="Closed Deals"
        value={closedDeals}
        icon={<Check className="h-4 w-4 text-green-500" />}
        description={`${successRate}% success rate`}
        progress={successRate}
        className="bg-green-50/50"
      />
      
      <MetricCard
        title="SMS Read Rate"
        value={`${viewRate}%`}
        icon={<Eye className="h-4 w-4 text-purple-500" />}
        description={`${viewedSMS} out of ${sentSMS} sent SMS`}
        progress={viewRate}
        className="bg-purple-50/50"
      />
      
      {emailsSent > 0 && (
        <MetricCard
          title="Email Open Rate"
          value={`${emailOpenRate.toFixed(1)}%`}
          icon={<Mail className="h-4 w-4 text-amber-500" />}
          description={`${emailsOpened} out of ${emailsSent} sent emails`}
          progress={emailOpenRate}
          className="bg-amber-50/50"
        />
      )}
      
      <MetricCard
        title="Recent Activity"
        value={leadsLastWeek}
        icon={<Calendar className="h-4 w-4 text-indigo-500" />}
        description="New leads in the last 7 days"
        className="bg-indigo-50/50"
      />
      
      <MetricCard
        title="Opportunity Score"
        value={Math.round((successRate + viewRate + (emailOpenRate > 0 ? emailOpenRate : 0)) / 3)}
        icon={<BarChart3 className="h-4 w-4 text-rose-500" />}
        description="Based on engagement & conversion"
        progress={(successRate + viewRate + (emailOpenRate > 0 ? emailOpenRate : 0)) / 3}
        className="bg-rose-50/50"
      />
    </div>
  );
};

export default PartnerDashboardMetrics;
